/**
 * Ensures a function is defined, throwing an error if it's not.
 * @param fn - The function to check
 * @param name - The name of the function for error messages
 * @returns The function if it's defined
 * @throws Error if the function is undefined
 */
const ensureDefined = <T extends Function>(fn: T | null, name: string): T => {
  if (!fn) {
    throw new Error(`${name} is not defined`);
  }
  return fn;
};

/**
 * Picks the correct path based on the context. This helps abstract the complex logic
 * of dealing with White Labeled Topics and FO/PO.
 */
export const pickPath = <TParams extends Array<unknown>>({
  paths,
  topicId,
  userId,
  isWLTopic,
}: {
  isWLTopic?: boolean;
  paths: {
    networkFO: ((...params: TParams) => string) | null;
    networkPO: ((...params: TParams) => string) | null;
    topicFO: ((topicId: number, ...params: TParams) => string) | null;
    topicPO: ((topicId: number, ...params: TParams) => string) | null;
  };
  topicId?: number | null;
  userId?: number | null;
}) => {
  const isFO = !!userId;
  const isPO = !isFO;
  const isTopic = !!topicId;
  const isWLTopicPO = isTopic && isWLTopic && isPO;
  const { networkFO, networkPO, topicFO, topicPO } = paths;

  return (...params: TParams): string => {
    if (isWLTopicPO) {
      return ensureDefined(networkPO, 'networkPO')(...params);
    }

    if (isTopic) {
      if (isFO) {
        return ensureDefined(topicFO, 'topicFO')(topicId, ...params);
      }

      return ensureDefined(topicPO, 'topicPO')(topicId, ...params);
    }

    if (isFO) {
      return ensureDefined(networkFO, 'networkFO')(...params);
    }

    return ensureDefined(networkPO, 'networkPO')(...params);
  };
};
